import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Monitor, CheckCircle, UtensilsCrossed, Zap } from 'lucide-react'
import { ImageGallery } from '../../common/ImageGallery'

export const BaresKDSSection: React.FC = () => {
  const { t } = useTranslation('bares')
  const [open, setOpen] = useState(false)

  const features = [
    t('kds.features.queue'),
    t('kds.features.status'),
    t('kds.features.splitStations'),
    t('kds.features.touch'),
    t('kds.features.printFallback')
  ]

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-20 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4"
          >
            <Monitor className="w-4 h-4" />
            {t('kds.kicker', 'Cozinha Digital')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {t('kds.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('kds.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border-2 border-orange-200 p-6 shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Monitor className="w-6 h-6 text-orange-600" />
              </div>
              <div className="font-bold text-gray-900 text-lg">{t('kds.mock.title')}</div>
            </div>
            <button className="w-full group" onClick={() => setOpen(true)}>
              <img
                src="/images/bares/12-kds-bar-desktop.png"
                alt="KDS - Bar"
                className="w-full rounded-xl border-2 border-white shadow-lg group-hover:shadow-2xl transition-all duration-300 group-hover:scale-[1.02]"
              />
            </button>
          </motion.div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6 shadow-lg"
            >
              <div className="flex items-center gap-2 text-gray-900 mb-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <UtensilsCrossed className="w-5 h-5 text-amber-600" />
                </div>
                <strong className="text-lg">{t('kds.stations.bar')}</strong>
              </div>
              <p className="text-gray-700">{t('kds.stations.barDesc')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl border border-orange-200 p-6 shadow-lg"
            >
              <div className="flex items-center gap-2 text-gray-900 mb-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <UtensilsCrossed className="w-5 h-5 text-orange-600" />
                </div>
                <strong className="text-lg">{t('kds.stations.kitchen')}</strong>
              </div>
              <p className="text-gray-700">{t('kds.stations.kitchenDesc')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl border-2 border-green-200 p-6 shadow-lg"
            >
              <div className="flex items-center gap-2 text-gray-900 mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600" />
                </div>
                <strong className="text-lg">{t('kds.features.title', 'Recursos')}</strong>
              </div>
              <ul className="space-y-3 text-gray-700">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
        <ImageGallery
          images={[{ src: '/images/bares/12-kds-bar-desktop.png', alt: 'KDS - Bar', title: 'KDS - Bar', description: 'Pedidos do bar em uma única tela, organizados por tempo.' }]}
          isOpen={open}
          onClose={() => setOpen(false)}
        />
      </div>
    </section>
  )
}
